import { cn } from '@/lib/utils'
import type {
  GlobalHomeWidgetLayoutEntry,
  GlobalHomeWidgetType,
  WidgetDefinition,
} from './widget-types'

type WidgetGridProps = {
  layout: GlobalHomeWidgetLayoutEntry[]
  definitions: Map<GlobalHomeWidgetType, WidgetDefinition>
}

type LayoutGroup =
  | { kind: 'full'; items: GlobalHomeWidgetLayoutEntry[] }
  | { kind: 'split'; left: GlobalHomeWidgetLayoutEntry[]; right: GlobalHomeWidgetLayoutEntry[] }

function groupLayout(layout: GlobalHomeWidgetLayoutEntry[]): LayoutGroup[] {
  const groups: LayoutGroup[] = []
  let i = 0

  while (i < layout.length) {
    const entry = layout[i]
    const isSplit = entry.column === 'left' || entry.column === 'right'

    if (!isSplit) {
      const items: GlobalHomeWidgetLayoutEntry[] = []
      while (i < layout.length) {
        const e = layout[i]
        if (e.column === 'left' || e.column === 'right') break
        items.push(e)
        i++
      }
      groups.push({ kind: 'full', items })
      continue
    }

    const left: GlobalHomeWidgetLayoutEntry[] = []
    const right: GlobalHomeWidgetLayoutEntry[] = []
    while (i < layout.length && (layout[i].column === 'left' || layout[i].column === 'right')) {
      if (layout[i].column === 'left') left.push(layout[i])
      else right.push(layout[i])
      i++
    }
    groups.push({ kind: 'split', left, right })
  }

  return groups
}

function spanClass(columns: number): string {
  const span = Math.min(12, Math.max(1, columns))
  if (span === 12) return 'col-span-12'
  if (span === 6) return 'col-span-12 @xl/home-feed:col-span-6'
  if (span === 4) return 'col-span-12 @xl/home-feed:col-span-4'
  return 'col-span-12 sm:col-span-6 @xl/home-feed:col-span-3'
}

function renderEntry(
  entry: GlobalHomeWidgetLayoutEntry,
  index: number,
  definitions: Map<GlobalHomeWidgetType, WidgetDefinition>,
  inColumn = false,
) {
  const def = definitions.get(entry.type)
  if (!def) return null
  return (
    <div
      key={`${entry.type}-${index}`}
      className={inColumn ? 'w-full min-w-0' : spanClass(entry.columns)}
    >
      {def.render({ config: entry.config })}
    </div>
  )
}

export function WidgetGrid({ layout, definitions }: WidgetGridProps) {
  const groups = groupLayout(layout)
  let entryIndex = 0

  return (
    <div className="space-y-6">
      {groups.map((group, groupIndex) => {
        if (group.kind === 'full') {
          return (
            <div key={`full-${groupIndex}`} className="grid grid-cols-12 gap-4 sm:gap-6">
              {group.items.map((entry) => {
                const node = renderEntry(entry, entryIndex++, definitions)
                return node
              })}
            </div>
          )
        }

        return (
          <div
            key={`split-${groupIndex}`}
            className="grid grid-cols-12 gap-4 sm:gap-6 items-start"
          >
            <div
              className={cn(
                'col-span-12 min-w-0 @xl/home-feed:col-span-6',
                'flex flex-col gap-4 sm:gap-6',
                group.left.length === 0 &&
                  'hidden @xl/home-feed:block @xl/home-feed:invisible @xl/home-feed:h-0 @xl/home-feed:overflow-hidden',
              )}
            >
              {group.left.map((entry) => renderEntry(entry, entryIndex++, definitions, true))}
            </div>
            <div className="col-span-12 min-w-0 @xl/home-feed:col-span-6 flex flex-col gap-4 sm:gap-6">
              {group.right.map((entry) => renderEntry(entry, entryIndex++, definitions, true))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
